// hooks/faq/useFaq.ts
import { useState, useEffect, useCallback } from "react";
import { Faq } from "../../../../types/features/vitrine/Faq";
import { faqService } from "../../../../services/vitrine/faqService";


const PRODUCT_NAME = 'izicallcenter';

export default function useFaqs() {
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Récupérer toutes les FAQ
  const fetchFaqs = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await faqService.getAll(page, perPage);
      setFaqs(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des FAQ");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Récupérer une FAQ par ID
  const fetchFaqById = useCallback(async (id: string): Promise<Faq | null> => {
    setLoading(true);
    setError(null);
    try {
      const faq = await faqService.getById(id);
      return faq;
    } catch (err) {
      setError("Erreur lors du chargement de la FAQ");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ NOUVEAU - Récupérer les FAQ par produit
  const fetchFaqsByProduct = useCallback(async (
    productName: string = PRODUCT_NAME,
    page: number = 1,
    perPage: number = 20,
    isVisible?: boolean
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await faqService.getByProductName(productName, page, perPage, isVisible);
      setFaqs(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des FAQ par produit");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ NOUVEAU - Récupérer les FAQ visibles par produit
  const fetchVisibleFaqsByProduct = useCallback(async (
    productName: string = PRODUCT_NAME,
    page: number = 1,
    perPage: number = 20
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await faqService.getVisibleByProductName(productName, page, perPage);
      setFaqs(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des FAQ visibles par produit");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ NOUVEAU - Récupérer les FAQ par produit et catégorie
  const fetchFaqsByProductAndCategory = useCallback(async (
    categoryId: string,
    productName: string = PRODUCT_NAME,
    page: number = 1,
    perPage: number = 20,
    isVisible?: boolean
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await faqService.getByProductAndCategory(
        productName,
        categoryId,
        page,
        perPage,
        isVisible
      );
      setFaqs(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des FAQ par catégorie");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ NOUVEAU - Récupérer les FAQ par produit avec filtres
  const fetchFaqsWithFilters = useCallback(async (
    filters: any = {},
    productName: string = PRODUCT_NAME,
    page: number = 1,
    perPage: number = 20
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await faqService.getByProductWithFilters(productName, filters, page, perPage);
      setFaqs(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des FAQ avec filtres");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisibleFaqsByProduct();
  }, []);

  return {
    faqs,
    loading,
    error,
    pagination,
    fetchFaqs,
    fetchFaqById,
    fetchFaqsByProduct,
    fetchVisibleFaqsByProduct,
    fetchFaqsByProductAndCategory,
    fetchFaqsWithFilters,
  };
}